/**
 * SmartMoneyPage — "copy the top trader" signal desk (Phase 1: Hyperliquid).
 *
 * Signals feed (a top trader just opened a position we can trade on Delta) with a
 * freshness gauge + one-click "Validate & Trade on chart"; a Traders tab (auto-followed
 * top-N by quality score + manual add/follow); a Positioning heatmap (net long/short
 * across followed traders); and Settings. Suggest-only — nothing auto-executes.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Tabs, Tab, Switch, FormControlLabel, Button,
  Chip, CircularProgress, Stack, Tooltip, TextField, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, Divider, Collapse,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import BoltIcon from '@mui/icons-material/Bolt';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import SettingsIcon from '@mui/icons-material/Settings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { copyTradeService } from '../services/copytrade.service';
import { usePersistedState } from '../hooks/usePersistedState';

const UP_C = '#26a69a';
const DOWN_C = '#ef5350';
const fmtUsd = (n: number) => n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `$${Math.round(n / 1e3)}k` : `$${Math.round(n)}`;
const pct = (n?: number | null) => n == null ? '—' : `${(Number(n) * 100).toFixed(1)}%`;
const timeAgo = (d?: string) => {
  if (!d) return '—';
  const s = Math.max(0, (Date.now() - new Date(d).getTime()) / 1000);
  if (s < 90) return `${Math.round(s)}s ago`;
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  if (s < 129600) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

const SmartMoneyPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [cfg, setCfg] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadCfg = useCallback(async () => {
    try { const r: any = await copyTradeService.getConfig(); setCfg(r.data?.data || null); }
    catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Failed to load Smart Money'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  const patchCfg = async (patch: Record<string, any>) => {
    try { const r: any = await copyTradeService.updateConfig(patch); setCfg(r.data?.data || null); }
    catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Update failed'); }
  };

  const scanNow = async () => {
    setBusy(true);
    try {
      const r: any = await copyTradeService.refreshNow();
      const d = r.data?.data || {};
      toast.success(`Scanned — ${d.autoFollowed ?? 0} auto-followed, ${d.newSignals ?? 0} new signal(s)`);
    } catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Scan failed'); }
    finally { setBusy(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  const enabled = !!cfg?.enabled;

  return (
    <Box sx={{ p: 2 }}>
      {/* Hero */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 2, background: 'linear-gradient(135deg, rgba(38,166,154,0.25), rgba(66,165,245,0.25))' }}>
          <GroupsIcon color="primary" />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
            Smart Money <span style={{ opacity: 0.6, fontWeight: 600, fontSize: '0.7em' }}>· copy signals</span>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: enabled ? UP_C : 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary">{enabled ? 'Engine running · Hyperliquid' : 'Engine off'}</Typography>
          </Box>
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} disabled={busy} onClick={scanNow}>{busy ? 'Scanning…' : 'Scan now'}</Button>
          <FormControlLabel control={<Switch checked={enabled} onChange={e => patchCfg({ enabled: e.target.checked })} />} label={enabled ? 'ON' : 'OFF'} />
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Follows top on-chain (Hyperliquid) traders and alerts you when they open a position you can trade on Delta —
        with a freshness check so you don't chase. Validate on the chart, then trade. <b>Suggest-only; nothing auto-executes.</b>
      </Typography>

      <HowItWorks />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minHeight: 46 } }} variant="scrollable" scrollButtons="auto">
        <Tab icon={<BoltIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Signals" />
        <Tab icon={<PersonSearchIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Traders" />
        <Tab icon={<DonutLargeIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Positioning" />
        <Tab icon={<SettingsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Settings" />
      </Tabs>

      {tab === 0 && <SignalsTab cfg={cfg} enabled={enabled} onScan={scanNow} busy={busy} />}
      {tab === 1 && <TradersTab />}
      {tab === 2 && <PositioningTab />}
      {tab === 3 && <SettingsTab cfg={cfg} patchCfg={patchCfg} />}
    </Box>
  );
};

/* ─────────────── How it works (in-page help) ─────────────── */
const HowItWorks: React.FC = () => {
  const [open, setOpen] = usePersistedState<boolean>('smartmoney.help.open', false);
  const steps: [string, string][] = [
    ['1 · Follow', 'Auto-follows the top-N Hyperliquid traders by a swing-biased quality score (account size, consistency, low churn — not raw PnL). Add any wallet manually in Traders.'],
    ['2 · Detect', 'Every few minutes it reads each followed trader’s on-chain fills and flags a fresh OPEN (or a flip) — the first poll is a baseline, so only positions taken AFTER you follow are copied.'],
    ['3 · Freshness', 'It compares the trader’s entry to the current price. If price already ran past your threshold it’s marked “chased” and won’t alert — so you don’t enter late.'],
    ['4 · Consensus', 'When several followed traders are on the same coin/side, the signal shows a consensus badge — stronger conviction for a swing.'],
    ['5 · Validate & Trade', 'Open the chart to confirm the setup, then place the trade yourself. Nothing auto-executes.'],
  ];
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.75, cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <HelpOutlineIcon sx={{ fontSize: 18, color: 'primary.main', mr: 1 }} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>How Smart Money works</Typography>
        <Box sx={{ ml: 'auto' }}><IconButton size="small">{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton></Box>
      </Box>
      <Collapse in={open}>
        <Divider />
        <Box sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {steps.map(([t, d]) => (
              <Box key={t} sx={{ flex: '1 1 180px', minWidth: 170 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: 'primary.main' }}>{t}</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.4 }}>{d}</Typography>
              </Box>
            ))}
          </Box>
          <Divider sx={{ my: 1 }} />
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
            <b>Crypto</b> signals come from Hyperliquid — real, on-chain, verifiable positions, mapped to Delta perps you can trade.
            <b> NSE</b> signals are UNVERIFIED social calls you (or a forwarding rule) paste in via the NSE box — treat them as ideas to validate, and watch each source’s hit-rate build over time.
          </Typography>
        </Box>
      </Collapse>
    </Card>
  );
};

/* ─────────────── Signals ─────────────── */
const SignalsTab: React.FC<{ cfg: any; enabled: boolean; onScan: () => void; busy: boolean }> = ({ cfg, enabled, onScan, busy }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [status, setStatus] = useState<'ACTIVE' | 'NEW' | 'ALL'>('ACTIVE');
  const [source, setSource] = useState<'ALL' | 'crypto' | 'nse'>('ALL');
  const [loading, setLoading] = useState(true);
  const [nseOpen, setNseOpen] = useState(false);
  const [nseSource, setNseSource] = useState('');
  const [nseText, setNseText] = useState('');
  const [nseBusy, setNseBusy] = useState(false);
  const navigate = useNavigate();
  const threshold = Number(cfg?.freshnessMaxMovePct ?? 0.75);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r: any = await copyTradeService.getSignals(status, source, 80); setRows(r.data?.data || []); }
    catch { /* tolerated */ } finally { setLoading(false); }
  }, [status, source]);
  useEffect(() => { load(); }, [load]);

  const ingestNse = async () => {
    if (!nseText.trim()) return;
    setNseBusy(true);
    try {
      const r: any = await copyTradeService.ingestNse(nseSource.trim() || 'Manual', nseText.trim());
      const d = r.data?.data || {};
      toast.success(`Added ${d.side === 'LONG' ? 'BUY' : 'SELL'} ${d.symbol}`);
      setNseText('');
      load();
    } catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Could not read that call'); }
    finally { setNseBusy(false); }
  };

  const act = async (id: number, s: string) => {
    try { await copyTradeService.setSignalStatus(id, s); setRows(prev => prev.filter(r => r.id !== id)); }
    catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Failed'); }
  };

  const moveMeta = (r: any) => {
    const m = Number(r.moveSinceEntryPct);
    if (r.status === 'EXPIRED' || m > threshold) return { color: DOWN_C, label: `chased ${m >= 0 ? '+' : ''}${m.toFixed(2)}%` };
    if (m <= 0) return { color: UP_C, label: `better ${m.toFixed(2)}%` };
    return { color: '#ed6c02', label: `+${m.toFixed(2)}% since entry` };
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress size={26} /></Box>;

  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {(['ACTIVE', 'NEW', 'ALL'] as const).map(s => (
          <Chip key={s} label={s === 'ACTIVE' ? 'Active' : s === 'NEW' ? 'New only' : 'All'} size="small"
            color={status === s ? 'primary' : 'default'} variant={status === s ? 'filled' : 'outlined'} onClick={() => setStatus(s)} />
        ))}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        {(['ALL', 'crypto', 'nse'] as const).map(s => (
          <Chip key={s} label={s === 'ALL' ? 'All sources' : s === 'crypto' ? 'Crypto' : 'NSE'} size="small"
            color={source === s ? 'secondary' : 'default'} variant={source === s ? 'filled' : 'outlined'} onClick={() => setSource(s)} />
        ))}
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" onClick={() => setNseOpen(o => !o)}>{nseOpen ? 'Close' : '+ NSE call'}</Button>
        <Tooltip title="Reload"><span><IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton></span></Tooltip>
      </Box>

      <Collapse in={nseOpen}>
        <Card variant="outlined" sx={{ borderColor: 'warning.main' }}><CardContent sx={{ py: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', mb: 0.5 }}>Paste an NSE call <Chip size="small" label="unverified" color="warning" sx={{ height: 16, fontSize: '0.55rem', ml: 0.5 }} /></Typography>
          <Typography variant="caption" color="text.secondary">
            Any tip you paste (or a Telegram forward rule / script POSTing to <code>/copytrade/nse/ingest</code>) becomes a signal here. e.g. “BUY RELIANCE 2900 SL 2850 TGT 2980”.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            <TextField size="small" label="Source / channel" value={nseSource} onChange={e => setNseSource(e.target.value)} sx={{ width: 180 }} placeholder="e.g. My tipster" />
            <TextField size="small" label="Call text" value={nseText} onChange={e => setNseText(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
              onKeyDown={e => { if (e.key === 'Enter') ingestNse(); }} placeholder="BUY RELIANCE 2900 SL 2850 TGT 2980" />
            <Button size="small" variant="contained" disabled={nseBusy} onClick={ingestNse}>{nseBusy ? 'Adding…' : 'Add call'}</Button>
          </Stack>
        </CardContent></Card>
      </Collapse>

      {rows.length === 0 ? (
        <Card variant="outlined"><CardContent>
          <Typography variant="body2" color="text.secondary">
            {!enabled ? 'Turn the engine ON (top-right), then ' : ''}hit <b>Scan now</b> to seed the leaderboard and auto-follow the top traders.
            Signals appear here when a followed trader opens a fresh position you can trade on Delta. New positions taken <i>after</i> you follow are what get copied.
          </Typography>
          {enabled && <Button sx={{ mt: 1 }} size="small" variant="contained" disabled={busy} onClick={onScan}>{busy ? 'Scanning…' : 'Scan now'}</Button>}
        </CardContent></Card>
      ) : rows.map(r => {
        const up = r.side === 'LONG';
        const col = up ? UP_C : DOWN_C;
        const isNse = r.source === 'nse';
        const mm = moveMeta(r);
        return (
          <Card key={r.id} variant="outlined" sx={{ borderLeft: `4px solid ${col}` }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" icon={up ? <TrendingUpIcon sx={{ fontSize: 15 }} /> : <TrendingDownIcon sx={{ fontSize: 15 }} />}
                  label={up ? 'LONG' : 'SHORT'} sx={{ height: 22, fontWeight: 800, bgcolor: col, color: '#fff', '& .MuiChip-icon': { color: '#fff' } }} />
                <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>{r.symbol}</Typography>
                <Chip size="small" variant="outlined" label={`${r.traderHandle || 'trader'}${r.traderRank ? ` · #${r.traderRank}` : ''}`} sx={{ height: 20, fontSize: '0.62rem' }} />
                {r.consensusCount > 1 && <Chip size="small" label={`${r.consensusCount} traders`} sx={{ height: 20, fontWeight: 700, bgcolor: 'rgba(66,165,245,0.18)', color: '#42a5f5' }} />}
                {isNse && <Chip size="small" label="NSE" variant="outlined" sx={{ height: 20, fontSize: '0.6rem' }} />}
                <Box sx={{ flex: 1 }} />
                {isNse
                  ? <Chip size="small" label="unverified" color="warning" variant="outlined" sx={{ height: 20, fontWeight: 700 }} />
                  : <Chip size="small" label={mm.label} sx={{ height: 20, fontWeight: 800, color: mm.color, borderColor: mm.color }} variant="outlined" />}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mt: 0.8, flexWrap: 'wrap' }}>
                {isNse ? (<>
                  {r.entryPx != null && <Metric k="Entry" v={`₹${Number(r.entryPx).toLocaleString('en-IN')}`} />}
                  {r.extra?.sl != null && <Metric k="Stop-loss" v={`₹${Number(r.extra.sl).toLocaleString('en-IN')}`} />}
                  {r.extra?.target != null && <Metric k="Target" v={`₹${Number(r.extra.target).toLocaleString('en-IN')}`} />}
                  <Metric k="Source" v={r.extra?.channel || r.traderHandle || '—'} />
                  <Metric k="Called" v={timeAgo(r.detectedAt)} />
                </>) : (<>
                  <Metric k="Trader entry" v={Number(r.entryPx).toLocaleString('en-US')} />
                  <Metric k="Now" v={Number(r.priceAtDetect).toLocaleString('en-US')} />
                  {r.bookFraction != null && <Metric k="Of their book" v={`${(Number(r.bookFraction) * 100).toFixed(0)}%`} />}
                  <Metric k="Entered" v={timeAgo(r.entryAt)} />
                  <Metric k="Detected" v={timeAgo(r.detectedAt)} />
                </>)}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1.2, flexWrap: 'wrap' }}>
                <Button size="small" variant="contained" startIcon={<CandlestickChartIcon />}
                  onClick={() => navigate(`/charts/${encodeURIComponent(r.symbol)}?exchange=${encodeURIComponent(r.exchange || 'DELTA')}`)}>
                  Validate &amp; Trade on chart
                </Button>
                <Button size="small" variant="outlined" color="success" onClick={() => act(r.id, 'TRADED')}>Mark traded</Button>
                <Button size="small" variant="text" color="inherit" onClick={() => act(r.id, 'DISMISSED')}>Dismiss</Button>
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
};

const Metric: React.FC<{ k: string; v: string }> = ({ k, v }) => (
  <Box>
    <Typography sx={{ fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary' }}>{k}</Typography>
    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{v}</Typography>
  </Box>
);

/* ─────────────── Traders ─────────────── */
const TradersTab: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [discover, setDiscover] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addr, setAddr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r: any = await copyTradeService.getTraders(discover); setRows(r.data?.data || []); }
    catch { /* tolerated */ } finally { setLoading(false); }
  }, [discover]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (t: any, follow: boolean) => {
    try { await copyTradeService.setFollow(t.id, follow); load(); }
    catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Failed'); }
  };
  const add = async () => {
    if (!addr.trim()) return;
    try { await copyTradeService.addTrader(addr.trim()); setAddr(''); toast.success('Trader added & followed'); setDiscover(false); load(); }
    catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Failed to add'); }
  };

  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip label="Followed" size="small" color={!discover ? 'primary' : 'default'} variant={!discover ? 'filled' : 'outlined'} onClick={() => setDiscover(false)} />
        <Chip label="Discover (top-ranked)" size="small" color={discover ? 'primary' : 'default'} variant={discover ? 'filled' : 'outlined'} onClick={() => setDiscover(true)} />
        <Box sx={{ flex: 1 }} />
        <TextField size="small" placeholder="Add wallet 0x…" value={addr} onChange={e => setAddr(e.target.value)} sx={{ width: 260 }} />
        <Button size="small" variant="outlined" onClick={add}>Add</Button>
      </Box>
      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><CircularProgress size={24} /></Box> : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>#</TableCell><TableCell>Trader</TableCell><TableCell align="right">Account</TableCell>
              <TableCell align="right">ROI (mo)</TableCell><TableCell align="right">ROI (all)</TableCell>
              <TableCell align="right">Score</TableCell><TableCell align="center">Mode</TableCell><TableCell align="center">Follow</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {rows.map(t => {
                const m = t.metrics || {};
                return (
                  <TableRow key={t.id} hover>
                    <TableCell>{t.rank ?? '—'}</TableCell>
                    <TableCell>
                      <Tooltip title={t.externalId} arrow><span style={{ fontWeight: 700 }}>{t.handle || t.externalId?.slice(0, 8)}</span></Tooltip>
                    </TableCell>
                    <TableCell align="right">{m.accountValue ? fmtUsd(m.accountValue) : '—'}</TableCell>
                    <TableCell align="right" sx={{ color: (m.roiMonth ?? 0) >= 0 ? UP_C : DOWN_C }}>{m.roiMonth != null ? pct(m.roiMonth) : '—'}</TableCell>
                    <TableCell align="right" sx={{ color: (m.roiAllTime ?? 0) >= 0 ? UP_C : DOWN_C }}>{m.roiAllTime != null ? pct(m.roiAllTime) : '—'}</TableCell>
                    <TableCell align="right">{t.score != null ? Number(t.score).toFixed(2) : '—'}</TableCell>
                    <TableCell align="center"><Chip size="small" label={t.followMode} sx={{ height: 18, fontSize: '0.58rem' }} /></TableCell>
                    <TableCell align="center"><Switch size="small" checked={!!t.followed} onChange={e => toggle(t, e.target.checked)} /></TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>{discover ? 'No ranked traders yet — hit “Scan now”.' : 'No followed traders yet. Turn the engine on + Scan, or add a wallet above.'}</Typography></TableCell></TableRow>}
            </TableBody>
          </Table>
        </Box>
      )}
    </Stack>
  );
};

/* ─────────────── Positioning ─────────────── */
const PositioningTab: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  useEffect(() => { (async () => { try { const r: any = await copyTradeService.getPositioning(); setRows(r.data?.data || []); } catch { /* */ } finally { setLoading(false); } })(); }, []);
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress size={26} /></Box>;
  if (!rows.length) return <Card variant="outlined"><CardContent><Typography variant="body2" color="text.secondary">No open positions across your followed traders yet.</Typography></CardContent></Card>;
  return (
    <Card variant="outlined"><CardContent>
      <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Where the smart money is positioned</Typography>
      <Typography variant="caption" color="text.secondary">Net long vs short across your followed traders, by tradable symbol. Click to open the chart.</Typography>
      <Box sx={{ mt: 1.5 }}>
        {rows.map((r) => {
          const total = r.longCount + r.shortCount || 1;
          const longPct = (r.longCount / total) * 100;
          return (
            <Box key={r.symbol} sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }} onClick={() => navigate(`/charts/${encodeURIComponent(r.symbol)}?exchange=DELTA`)}>{r.symbol}</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: UP_C }}>{r.longCount} long</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: DOWN_C }}>{r.shortCount} short</Typography>
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: r.netCount >= 0 ? UP_C : DOWN_C }}>{r.netCount >= 0 ? 'net long' : 'net short'} ({r.netCount >= 0 ? '+' : ''}{r.netCount})</Typography>
              </Box>
              <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', bgcolor: 'action.hover' }}>
                <Box sx={{ width: `${longPct}%`, bgcolor: UP_C }} />
                <Box sx={{ width: `${100 - longPct}%`, bgcolor: DOWN_C }} />
              </Box>
            </Box>
          );
        })}
      </Box>
    </CardContent></Card>
  );
};

/* ─────────────── Settings ─────────────── */
const NumberField: React.FC<{ label: string; value: any; onCommit: (v: number) => void; help?: string; step?: string; width?: number }> =
  ({ label, value, onCommit, help, step, width = 170 }) => {
    const [v, setV] = useState(String(value ?? ''));
    useEffect(() => { setV(String(value ?? '')); }, [value]);
    return (
      <Tooltip title={help || ''} arrow>
        <TextField size="small" type="number" label={label} sx={{ width }} value={v} inputProps={{ step: step || 'any' }}
          onChange={e => setV(e.target.value)} onBlur={() => { const n = Number(v); if (Number.isFinite(n)) onCommit(n); }} />
      </Tooltip>
    );
  };

const SettingsTab: React.FC<{ cfg: any; patchCfg: (p: any) => void }> = ({ cfg, patchCfg }) => {
  const route = cfg?.alertRoute || {};
  return (
    <Stack spacing={2}>
      <Card variant="outlined"><CardContent>
        <Typography sx={{ fontWeight: 800, mb: 1 }}>Following & freshness</Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <NumberField label="Auto-follow top-N" value={cfg?.autoFollowTopN} onCommit={v => patchCfg({ autoFollowTopN: Math.max(0, Math.round(v)) })} help="Auto-follow the top N traders by quality score (0 = manual only)." />
          <NumberField label="Freshness max move %" value={cfg?.freshnessMaxMovePct} onCommit={v => patchCfg({ freshnessMaxMovePct: v })} step="0.05" help="Signal only if price hasn't moved more than this % in the trade direction since the trader's entry. Beyond it = 'chased', no alert." />
          <NumberField label="Min account value $" value={cfg?.minAccountValueUsd} width={190} onCommit={v => patchCfg({ minAccountValueUsd: Math.max(0, v) })} help="Only auto-follow traders with at least this account value — filters out tiny gamblers." />
          <NumberField label="Min book fraction" value={cfg?.minBookFraction} step="0.01" onCommit={v => patchCfg({ minBookFraction: Math.max(0, v) })} help="Ignore entries smaller than this fraction of the trader's account (noise)." />
          <NumberField label="Poll seconds" value={cfg?.pollSeconds} onCommit={v => patchCfg({ pollSeconds: Math.max(60, Math.round(v)) })} help="How often each followed trader is polled (60–900s). 180s ≈ within your 5–15 min swing budget." />
        </Stack>
        <FormControlLabel sx={{ mt: 1 }} control={<Switch checked={!!cfg?.swingOnly} onChange={e => patchCfg({ swingOnly: e.target.checked })} />} label="Bias auto-follow toward swing traders (down-rank hyper-scalpers)" />
      </CardContent></Card>

      <Card variant="outlined"><CardContent>
        <Typography sx={{ fontWeight: 800, mb: 1 }}>Alerts</Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <NumberField label="Min consensus for alert" value={cfg?.minConsensusForAlert} onCommit={v => patchCfg({ minConsensusForAlert: Math.max(1, Math.round(v)) })} width={200} help="Raise an alert only when at least this many followed traders are on the same coin/side." />
          <FormControlLabel control={<Switch checked={!!route.dashboard} onChange={e => patchCfg({ alertRoute: { ...route, dashboard: e.target.checked } })} />} label="Dashboard" />
          <FormControlLabel control={<Switch checked={!!route.telegram} onChange={e => patchCfg({ alertRoute: { ...route, telegram: e.target.checked } })} />} label="Telegram" />
        </Stack>
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="caption" color="text.secondary">
          Source: Hyperliquid (on-chain perp positions — public, real-money, verifiable). Signals map to Delta perps you can trade; unmapped coins are hidden.
          Suggest-only — you validate every signal on the chart and place the trade yourself.
        </Typography>
      </CardContent></Card>
    </Stack>
  );
};

export default SmartMoneyPage;
